/**
 * Early-morning booking confirm dialog.
 *
 * Members regularly misread a 1–5 AM slot as PM. Shown once, on the actual
 * submit action, after every other gate (waiver, price, single-submit) has
 * already passed. "Go Back" / Escape submits nothing; "Yes, Continue"
 * re-enters the caller's own handler (see useEarlyBookingConfirm).
 *
 * Composed from the AlertDialog primitive rather than ConfirmDialog: that
 * component's confirm button paints fixed Butter brand purple, which breaks
 * tenant theming on this member-facing surface.
 *
 * @module pages/booking/components
 */

import { Button } from '@butter/ui/primitives/actions';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@butter/ui/primitives/feedback';
import { EarlyBookingNotice } from '@butter/ui/domains/booking';

export interface EarlyBookingConfirmDialogProps {
  open: boolean;
  /** Club-local slot start, "HH:mm". */
  startTime: string | null;
  /** Club-local slot end, "HH:mm". */
  endTime: string | null;
  onConfirm: () => void;
  /** Go Back, overlay Escape, or the dialog's own dismiss. */
  onCancel: () => void;
}

export function EarlyBookingConfirmDialog({
  open,
  startTime,
  endTime,
  onConfirm,
  onCancel,
}: EarlyBookingConfirmDialogProps) {
  if (!startTime || !endTime) return null;

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <AlertDialogContent className="p-8" data-testid="early-booking-dialog">
        <EarlyBookingNotice
          startTime={startTime}
          endTime={endTime}
          titleAs={AlertDialogTitle}
          descriptionAs={AlertDialogDescription}
        />
        <AlertDialogFooter className="mt-6 flex w-full flex-col-reverse gap-3 sm:flex-row">
          <AlertDialogCancel asChild>
            <Button
              variant="themeOutline"
              size="xl"
              data-testid="early-booking-cancel"
              className="w-full text-butter-gray-600 sm:flex-1"
            >
              Go Back
            </Button>
          </AlertDialogCancel>
          <Button
            variant="theme"
            size="xl"
            data-testid="early-booking-confirm"
            className="w-full sm:flex-1"
            onClick={onConfirm}
          >
            Yes, Continue
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
